import { AntDesign } from "@expo/vector-icons";
import { of } from "@nozbe/watermelondb/utils/rx";
import {
  withObservables,
  ExtractedObservables,
} from "@nozbe/watermelondb/react";
import { useNavigation } from "@react-navigation/native";
import { SaleAdjustmentType } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { useCallback } from "react";
import { Colors } from "react-native-ui-lib";
import { compose } from "recompose";
import Sale from "../../../../../../utilities/data/model/Sale";
import { PageItemType } from "../../../../../../utilities/layout";
import { moneyToFormattedString } from "../../../../../../utilities/money";
import { SellScreenProps } from "../../../../screens/SellScreen";
import { withSaleController } from "../../../../utils/sale-controller";
import { QuickKey as BaseQuickKey, QuickKeyProps } from "../../../QuickKey";
import { useApplyTradePriceToSaleItems } from "../../../../utils/sale-item";

type ApplySaleTradePriceQuickKeyRendererProps = ExtractedObservables<
  ReturnType<typeof getObserables>
> & {
  sale?: Sale;
  item: PageItemType;
  innerRef?: React.MutableRefObject<any>;
} & QuickKeyProps;

const getObserables = ({ sale }: { sale: Sale }) => ({
  items: sale ? sale.items.observeWithColumns(["adjustments"]) : of(null),
});
export const ApplySaleTradePriceQuickKeyRenderer = React.forwardRef<
  any,
  ApplySaleTradePriceQuickKeyRendererProps
>(({ sale, items, item, innerRef, disabled: _disabled, ...rest }, ref) => {
  const [savings, setSavings] = React.useState(0);
  const [canModifySale, setCanModifySale] = React.useState(false);
  const { applyTradePriceToSaleItems } = useApplyTradePriceToSaleItems();

  React.useEffect(() => {
    (async () => {
      if (sale) {
        const r = await sale.canModifySale();
        setCanModifySale(r);
        if (r) {
          setSavings(await sale.calculatePotentialTradePriceSavings());
        } else {
          setSavings(0);
        }
      }
    })();
  }, [sale, items]);

  const navigation = useNavigation<SellScreenProps["navigation"]>();

  const disabled =
    _disabled !== undefined
      ? _disabled
      : canModifySale == false ||
        !sale ||
        !items ||
        items.length === 0 ||
        !savings;

  const isRemoveDiscount = items?.some((item) =>
    item.adjustments.find(
      (i) => i.type === SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT
    )
  );

  const title = isRemoveDiscount
    ? "Remove Trade Pricing"
    : "Apply Trade Pricing";

  const adjustmentTotal = items?.reduce((acc, item) => {
    const adjustment = item.adjustments.find(
        (i) => i.type === SaleAdjustmentType.TRADE_PRICE_ADJUSTMENT
      ),
      adjustmentTotal = adjustment ? adjustment.amount * item.quantity : 0;

    return acc + adjustmentTotal;
  }, 0);

  const handleOnPress = useCallback(() => {
    if (disabled) return;
    if (isRemoveDiscount) {
      return sale
        ?.removeSaleTradePriceAdjustments()
        .catch((e) => console.log(e));
    }
    if (sale) {
      return applyTradePriceToSaleItems(sale).catch((e) => console.log(e));
    }
  }, [sale, isRemoveDiscount, disabled, navigation]);

  const removalSubtitle = isRemoveDiscount
    ? moneyToFormattedString(adjustmentTotal)
    : undefined;

  const savingsSubtitle = !isRemoveDiscount
    ? `Save ${moneyToFormattedString(savings)}`
    : undefined;

  return (
    <BaseQuickKey
      ref={ref}
      disabled={disabled}
      isDestructive={isRemoveDiscount}
      backgroundColor={
        isRemoveDiscount
          ? Colors.quickKeyDestructionText
          : Colors.quickKeyLightgreenBackground
      }
      isModifier={!isRemoveDiscount}
      iconPrimary={
        <AntDesign
          name="tagso"
          size={24}
          color={
            disabled
              ? Colors.white
              : isRemoveDiscount
              ? Colors.quickKeyDestructionText
              : "#7dd49f"
          }
        />
      }
      onPress={handleOnPress}
      label={title}
      subtitle={removalSubtitle || savingsSubtitle}
      {...rest}
    />
  );
});

export const ApplySaleTradePriceQuickKey = compose(
  withSaleController,
  withObservables(["sale"], getObserables)
)(ApplySaleTradePriceQuickKeyRenderer as any);
